// ══════════════════════════════════════════════════════════════════════════
//  ACCESO: el motor puro de entrar, recuperar y guardar contraseña
//
//  Hasta el 2026-09-16 la pantalla de entrada decidía por el TEXTO del error y
//  todo fallo se leía como «contraseña incorrecta»: una cuenta bloqueada, la
//  tablet sin señal, el límite de intentos del servidor y el servicio caído.
//  Auth (GoTrue v2.197) sí distingue cada caso, pero en un campo que nadie leía
//  (error_code). Aquí se decide POR CÓDIGO, en un solo lugar, y de aquí salen
//  los textos que ve la persona.
//
//  Sin React, sin sb y sin la hoja de diseño: se prueba con vitest
//  (acceso.logic.test.js) con los cuerpos reales de Auth.
//
//  Qué vive aquí:
//    - clasificarFalloAuth: el fallo de cualquier llamada de sb.auth.* (o su
//      forma cruda { status, cuerpo, errorRed }) → clase, código y datos útiles.
//    - clasificarRenovacion y motivoDeRechazo: el resultado de renovar la
//      sesión (ok, rechazado o sin_red) y por qué se cerró.
//    - textoAcceso: la ÚNICA fuente de los textos de acceso.
//    - leerEnlaceAuth y urlSinParametrosAuth: el regreso desde un correo.
//    - POLITICA_CONTRASENA, reglasContrasena y los validar* del envío.
//    - decidirPerfil, iniciales, el bloqueo local y urlRestablecer.
//    - leerJwt, subDelToken y almacenEsDe: de quién es un token y de quién es la
//      sesión guardada en el equipo.
//
//  Forma uniforme de las llamadas de auth (la devuelve supabaseClient.js):
//
//  @typedef {Object} ResultadoAuth
//  @property {boolean} ok          respuesta 2xx con cuerpo legible
//  @property {number}  status      código HTTP; 0 cuando no hubo respuesta
//  @property {Object|null} datos   cuerpo JSON del éxito (null en 204 y en fallo)
//  @property {Object|string|null} cuerpo  cuerpo del fallo: el JSON de Auth, el
//                                  texto crudo si no era JSON (un 502 con HTML) o null
//  @property {null|'sin_red'|'tiempo_agotado'} errorRed  por qué no hubo respuesta
// ══════════════════════════════════════════════════════════════════════════

import { SITIO } from '../sitio.js';

// ── Cuerpos de Auth ───────────────────────────────────────────────────────
// GoTrue responde en tres formas y las tres se leen:
//   { code: 400, error_code: 'invalid_credentials', msg: '...' }   (la de hoy)
//   { code: 'invalid_credentials', message: '...' }                 (cabecera 2024-01-01)
//   { error: 'invalid_request', error_description: '...' }          (OAuth)
// weak_password agrega { weak_password: { reasons: [...] } } en las dos primeras.

function leerCuerpo(cuerpo) {
  if (cuerpo && typeof cuerpo === 'object') return cuerpo;
  if (typeof cuerpo === 'string') {
    const t = cuerpo.trim();
    if (t.charAt(0) === '{') {
      try {
        const j = JSON.parse(t);
        if (j && typeof j === 'object') return j;
      } catch (e) { /* no era JSON: un proxy o una página de error */ }
    }
  }
  return null;
}

function codigoDe(c) {
  if (!c) return null;
  if (typeof c.error_code === 'string' && c.error_code) return c.error_code;
  // code numérico (o "200" como texto) es el estado HTTP, no el código de error
  if (typeof c.code === 'string' && c.code && !/^\d+$/.test(c.code)) return c.code;
  if (typeof c.error === 'string' && c.error) return c.error;
  return null;
}

function mensajeDe(c, cuerpoCrudo) {
  if (c) {
    for (const k of ['msg', 'message', 'error_description']) {
      if (typeof c[k] === 'string' && c[k]) return c[k];
    }
    if (typeof c.error === 'string' && c.error) return c.error;
  }
  return typeof cuerpoCrudo === 'string' ? cuerpoCrudo : '';
}

function razonesDe(c) {
  const r = c && c.weak_password && c.weak_password.reasons;
  return Array.isArray(r) ? r.filter((x) => typeof x === 'string') : [];
}

// «For security purposes, you can only request this after 42 seconds.»
function segundosDeEspera(mensaje) {
  const m = /after\s+(\d+)\s+seconds?/i.exec(String(mensaje || ''));
  return m ? Number(m[1]) : null;
}

/**
 * Tipo de falla sin respuesta. Acepta el valor ya normalizado
 * ('sin_red' | 'tiempo_agotado'), true, o la excepción de fetch.
 * TypeError = no hubo red; AbortError/TimeoutError = se cortó por tiempo.
 */
export function tipoFalloRed(e) {
  if (!e) return null;
  if (e === 'sin_red' || e === 'tiempo_agotado') return e;
  if (e === true) return 'sin_red';
  const nombre = typeof e === 'object' ? e.name : null;
  if (nombre === 'AbortError' || nombre === 'TimeoutError') return 'tiempo_agotado';
  if ((typeof TypeError !== 'undefined' && e instanceof TypeError) || nombre === 'TypeError') return 'sin_red';
  return null;
}

// Código de Auth → clase de acceso. Lo que no está aquí se decide por estado HTTP.
const CLASE_POR_CODIGO = {
  invalid_credentials: 'credenciales',
  user_banned: 'acceso_desactivado',
  email_not_confirmed: 'correo_sin_confirmar',
  over_request_rate_limit: 'limite_intentos',
  over_email_send_rate_limit: 'limite_correos',
  email_address_not_authorized: 'correo_no_autorizado',
  weak_password: 'contrasena_debil',
  same_password: 'contrasena_repetida',
  current_password_required: 'contrasena_actual_requerida',
  // En v2.197 la constante se llama CurrentPasswordMismatch pero su valor es
  // current_password_invalid; se aceptan los dos nombres.
  current_password_invalid: 'contrasena_actual_incorrecta',
  current_password_mismatch: 'contrasena_actual_incorrecta',
  reauthentication_needed: 'reautenticacion',
  reauthentication_not_valid: 'reautenticacion',
  otp_expired: 'enlace_vencido',
  flow_state_not_found: 'enlace_vencido',
  flow_state_expired: 'enlace_vencido',
  flow_state_already_used: 'enlace_vencido',
  invite_not_found: 'enlace_vencido',
  bad_jwt: 'sesion_invalida',
  no_authorization: 'sesion_invalida',
  session_not_found: 'sesion_invalida',
  session_expired: 'sesion_invalida',
  refresh_token_not_found: 'sesion_invalida',
  refresh_token_already_used: 'sesion_invalida',
  user_not_found: 'sesion_invalida',
  validation_failed: 'datos_invalidos',
  email_address_invalid: 'datos_invalidos',
  captcha_failed: 'acceso_no_disponible',
  email_provider_disabled: 'acceso_no_disponible',
  provider_disabled: 'acceso_no_disponible',
  signup_disabled: 'acceso_no_disponible',
  otp_disabled: 'acceso_no_disponible',
  feature_disabled: 'acceso_no_disponible',
  insufficient_aal: 'acceso_no_disponible',
  user_already_exists: 'cuenta_existente',
  email_exists: 'cuenta_existente',
  unexpected_failure: 'servicio',
  request_timeout: 'servicio',
  hook_timeout: 'servicio',
  hook_timeout_after_retry: 'servicio',
};

/**
 * Clasifica el fallo de una llamada de auth.
 * @param {ResultadoAuth|{status:number, cuerpo:*, errorRed:*}} resultado
 * @returns {null|{clase:string, codigo:string|null, status:number, esperaSeg:number|null,
 *   razones:string[], cuentaParaBloqueo:boolean, mensaje:string}}
 *   null si el resultado fue un éxito. `mensaje` es el texto de Auth (en inglés):
 *   sirve para la consola, nunca para la pantalla.
 */
export function clasificarFalloAuth(resultado) {
  const r = resultado || {};
  if (r.ok) return null;
  const status = Number(r.status) || 0;
  const c = leerCuerpo(r.cuerpo);
  const codigo = codigoDe(c);
  const mensaje = mensajeDe(c, r.cuerpo);
  const razones = razonesDe(c);
  const red = tipoFalloRed(r.errorRed);

  let clase;
  if (red === 'tiempo_agotado') clase = 'servicio';
  else if (red === 'sin_red' || status === 0) clase = 'sin_conexion';
  else if (status >= 500) clase = 'servicio';
  else if (codigo && CLASE_POR_CODIGO[codigo]) clase = CLASE_POR_CODIGO[codigo];
  else if (status === 429) clase = 'limite_intentos';
  else if (razones.length) clase = 'contrasena_debil';
  else if (!c) clase = 'servicio'; // no respondió Auth: HTML de un proxy, cuerpo vacío o ilegible
  else if (status === 401 || status === 403) clase = 'sesion_invalida'; // con el enlace: venció o ya se usó
  else clase = 'desconocido';

  return {
    clase,
    codigo,
    status,
    esperaSeg: segundosDeEspera(mensaje),
    razones,
    cuentaParaBloqueo: clase === 'credenciales',
    mensaje,
  };
}

/**
 * Resultado de renovar la sesión (POST /token?grant_type=refresh_token).
 * - ok:        llegó una sesión nueva.
 * - rechazado: Auth dijo que esa sesión ya no sirve (400/401 con su código:
 *              refresh_token_not_found, refresh_token_already_used,
 *              session_not_found, user_banned...). Solo este cierra la sesión.
 * - sin_red:   no se sabe (sin respuesta, tiempo agotado, 429, 5xx o una
 *              respuesta que no es de Auth). La sesión se conserva.
 */
export function clasificarRenovacion(resultado) {
  const r = resultado || {};
  if (r.ok) return r.datos && r.datos.access_token ? 'ok' : 'sin_red';
  if (r.errorRed || !r.status) return 'sin_red';
  if (r.status === 400 || r.status === 401) return codigoDe(leerCuerpo(r.cuerpo)) ? 'rechazado' : 'sin_red';
  return 'sin_red';
}

/** Motivos con los que se avisa un cierre de sesión (setOnSessionExpired). Cada uno es una clase de textoAcceso. */
export const MOTIVOS_CIERRE = Object.freeze(['sesion_cerrada', 'por_seguridad', 'acceso_desactivado']);

/** Por qué se cerró una sesión rechazada: sesion_cerrada | por_seguridad | acceso_desactivado. */
export function motivoDeRechazo(resultado) {
  const codigo = codigoDe(leerCuerpo(resultado && resultado.cuerpo));
  if (codigo === 'refresh_token_already_used') return 'por_seguridad';
  if (codigo === 'user_banned') return 'acceso_desactivado';
  return 'sesion_cerrada';
}

// ── Textos ────────────────────────────────────────────────────────────────

/** Modos de la pantalla. confirmar = pedir la contraseña para una acción delicada (Republicar). */
export const MODOS_ACCESO = Object.freeze(['entrar', 'recuperar', 'enviado', 'nueva', 'crear', 'cambiar', 'confirmar']);

export const TITULOS_ACCESO = Object.freeze({
  recuperar: 'Recuperar contraseña',
  enviado: 'Revisa tu correo',
  nueva: 'Nueva contraseña',
  crear: 'Crea tu contraseña',
  cambiar: 'Cambiar contraseña',
});

/** Segundos antes de ofrecer Reenviar: Auth rechaza otro envío al mismo correo antes de 60 s. */
export const REENVIO_SEG = 60;

const MODOS_CONTRASENA = new Set(['nueva', 'crear', 'cambiar']);
const MODOS_ENLACE = new Set(['nueva', 'crear']);
const ACCION_PEDIR_ENLACE = Object.freeze({ id: 'pedir_enlace', texto: 'Pedir otro enlace' });
const ACCION_ENTRAR = Object.freeze({ id: 'entrar', texto: 'Volver a iniciar sesión' });

const TEXTO_SESION_CERRADA = 'Se cerró tu sesión. Puede ser por un cambio de contraseña, por el administrador o desde otro equipo.';
const TEXTO_ACCESO_DESACTIVADO = 'No pudimos iniciar sesión con esta cuenta. Habla con el administrador.';
const TEXTO_ENLACE_VENCIDO = 'El enlace venció o ya se usó.';

function textoGenerico(modo) {
  if (modo === 'recuperar' || modo === 'enviado') return 'No pudimos enviar el enlace. Inténtalo de nuevo.';
  if (MODOS_CONTRASENA.has(modo)) return 'No pudimos guardar tu contraseña. Inténtalo de nuevo.';
  if (modo === 'confirmar') return 'No pudimos confirmar tu contraseña. Inténtalo de nuevo.';
  return 'No pudimos iniciar sesión. Inténtalo de nuevo.';
}

function textoSinConexion(modo) {
  if (modo === 'recuperar' || modo === 'enviado') return 'Sin conexión. Para pedir el enlace necesitas internet.';
  if (MODOS_CONTRASENA.has(modo)) return 'Sin conexión. Para guardar tu contraseña necesitas internet.';
  if (modo === 'confirmar') return 'Sin conexión. Para confirmar tu contraseña necesitas internet.';
  return 'Sin conexión. Para entrar necesitas internet.';
}

/**
 * Qué se le dice a la persona. Única fuente de los textos de acceso.
 * @param {string} clase   de clasificarFalloAuth, de un validar*, de decidirPerfil
 *                         o un motivo de cierre (sesion_cerrada, por_seguridad,
 *                         acceso_desactivado); también contrasena_guardada, contrasena_actualizada
 *                         y enlace_enviado.
 * @param {{modo?:string, esperaSeg?:number|null, correo?:string, razones?:string[]}} [opciones]
 * @returns {{clase:string, texto:string, campo:null|'correo'|'contrasena'|'confirmacion'|'actual',
 *   tono:'error'|'info', accion:null|{id:string, texto:string}, esperaRed:boolean,
 *   titulo?:string, nota?:string, reenviar?:{id:string, texto:string, segundos:number}}}
 *   campo null = aviso de cuenta o de red (una línea bajo el saludo); con campo, va bajo ese campo.
 *   esperaRed = el aviso se quita solo cuando vuelve la red.
 */
export function textoAcceso(clase, opcionesDadas) {
  const opciones = opcionesDadas || {};
  const modo = opciones.modo || 'entrar';
  const aviso = (texto, extra) => ({ clase, texto, campo: null, tono: 'error', accion: null, esperaRed: false, ...extra });
  const enCampo = (campo, texto, extra) => aviso(texto, { campo, ...extra });
  const enlaceVencido = () => aviso(TEXTO_ENLACE_VENCIDO, { accion: ACCION_PEDIR_ENLACE });

  switch (clase) {
    // Lo que falta al enviar (no llega al servidor)
    case 'falta_correo': return enCampo('correo', 'Escribe tu correo electrónico.');
    case 'correo_invalido': return enCampo('correo', 'Escribe un correo electrónico válido.');
    case 'falta_contrasena':
      return enCampo('contrasena', MODOS_CONTRASENA.has(modo) ? 'Escribe tu contraseña nueva.' : 'Escribe tu contraseña.');
    case 'falta_actual': return enCampo('actual', 'Escribe tu contraseña actual.');
    case 'falta_confirmacion': return enCampo('confirmacion', 'Vuelve a escribir la contraseña nueva.');
    case 'no_coinciden': return enCampo('confirmacion', 'Las contraseñas no coinciden.');
    case 'contrasena_larga': return enCampo('contrasena', 'La contraseña es demasiado larga.');

    // Contraseña
    case 'contrasena_debil': {
      const razones = opciones.razones || [];
      if (razones.length && razones.every((x) => x === 'pwned')) {
        return enCampo('contrasena', 'Esa contraseña es fácil de adivinar. Elige otra.');
      }
      return enCampo('contrasena', 'La contraseña no cumple las reglas.');
    }
    case 'contrasena_repetida': return enCampo('contrasena', 'Usa una contraseña distinta a la anterior.');
    case 'contrasena_actual_requerida': return enCampo('actual', 'Escribe tu contraseña actual.');
    case 'contrasena_actual_incorrecta': return enCampo('actual', 'La contraseña actual no es correcta.');
    case 'reautenticacion': return aviso('Para cambiar tu contraseña, cierra sesión y vuelve a entrar.');

    // Entrar
    case 'credenciales':
      if (modo === 'cambiar') return enCampo('actual', 'La contraseña actual no es correcta.');
      if (modo === 'confirmar') return enCampo('contrasena', 'La contraseña no es correcta.');
      return enCampo('contrasena', 'Correo electrónico o contraseña incorrectos.');
    case 'acceso_desactivado': return aviso(TEXTO_ACCESO_DESACTIVADO);
    case 'correo_sin_confirmar': return aviso('Tu correo aún no está confirmado. Habla con el administrador.');
    case 'limite_intentos': return aviso('Demasiados intentos desde esta red. Espera unos minutos e inténtalo de nuevo.');
    // El bloqueo local ya se cuenta en el botón («Espera N s»); este texto es para la región
    // de avisos, que lo lee cuando se intenta entrar con el bloqueo activo.
    case 'bloqueo_local': {
      const n = Number(opciones.esperaSeg);
      return aviso(n > 0 ? `Espera ${n} s para volver a intentarlo.` : 'Espera unos segundos para volver a intentarlo.', { tono: 'info' });
    }
    case 'sin_perfil': return aviso('Tu cuenta no tiene perfil configurado. Habla con el administrador.');
    case 'pendiente': return aviso('Tu cuenta está pendiente de aprobación por un administrador.');
    case 'acceso_no_disponible': return aviso('El acceso con correo no está disponible por ahora. Habla con el administrador.');
    case 'cuenta_existente': return aviso('Ya hay una cuenta con ese correo.');

    // Recuperar
    case 'limite_correos': {
      const n = Number(opciones.esperaSeg);
      return aviso(n > 0
        ? `Ya se enviaron varios correos. Espera ${n} s para pedir otro.`
        : 'Ya se enviaron varios correos. Espera unos minutos para pedir otro.');
    }
    case 'correo_no_autorizado': return aviso('No pudimos enviar el correo a esta dirección. Habla con el administrador.');
    case 'enlace_enviado': {
      const correo = String(opciones.correo || '').trim();
      return aviso(
        `Si hay una cuenta con ${correo}, te enviamos un enlace para crear una contraseña nueva. Usa el correo más reciente: el enlace vence en una hora.`,
        {
          tono: 'info',
          titulo: 'Revisa tu correo',
          nota: 'Revisa también la carpeta de spam.',
          accion: ACCION_ENTRAR,
          reenviar: { id: 'reenviar', texto: 'Reenviar', segundos: REENVIO_SEG },
        },
      );
    }

    // Enlace del correo y sesión
    case 'enlace_vencido': return enlaceVencido();
    case 'sesion_invalida':
      // Con el token del enlace, una sesión inválida es un enlace que ya no sirve.
      return MODOS_ENLACE.has(modo) ? enlaceVencido() : aviso(TEXTO_SESION_CERRADA, { tono: 'info' });
    case 'datos_invalidos':
      if (modo === 'entrar' || modo === 'recuperar' || modo === 'enviado') return enCampo('correo', 'Escribe un correo electrónico válido.');
      if (MODOS_ENLACE.has(modo)) return enlaceVencido();
      return aviso(textoGenerico(modo));
    case 'sesion_cerrada': return aviso(TEXTO_SESION_CERRADA, { tono: 'info' });
    case 'por_seguridad': return aviso('Tu sesión se cerró por seguridad. Vuelve a entrar.', { tono: 'info' });
    case 'contrasena_guardada': return aviso('Contraseña guardada. Entra con tu contraseña nueva.', { tono: 'info' });
    case 'contrasena_actualizada': return aviso('Contraseña actualizada', { tono: 'info' });
    // La vista de permiso sin sesión (2026-10-28): por qué se pide entrar, sin nombrar a
    // quien pide (los detalles de la solicitud solo se leen con sesión).
    case 'permiso_solicitud': return aviso('Una aplicación pide usar GEOBAJA con tu cuenta. Entra para ver cuál y decidir.', { tono: 'info' });

    // Red y servicio
    case 'sin_conexion': return aviso(textoSinConexion(modo), { esperaRed: true });
    case 'servicio': return aviso('El servicio de acceso no responde. Inténtalo en unos minutos.');

    default: return aviso(textoGenerico(modo));
  }
}

// ── Enlaces del correo ────────────────────────────────────────────────────

// Plantilla nueva: /restablecer?token_hash=...&type=recovery|invite (no gasta el
// token al abrir). Heredado: /#access_token=...&type=recovery (Auth ya lo gastó).
const TIPOS_CON_TOKEN_HASH = ['recovery', 'invite'];
const TIPOS_CON_SESION = ['recovery', 'invite', 'magiclink'];
const CLAVES_QUERY_AUTH = ['token_hash', 'type', 'error', 'error_code', 'error_description', 'sb'];
// El estado HTTP que Auth tradujo a `error` al redirigir (oauthErrorMap de verify.go).
const ESTADO_DE_ERROR_OAUTH = {
  invalid_request: 400,
  unauthorized_client: 401,
  access_denied: 403,
  server_error: 500,
  temporarily_unavailable: 503,
};

export const RUTA_RESTABLECER = '/restablecer';

/** Destino del enlace de recuperación: el sitio público (sitio.js), nunca window.location.origin. */
export function urlRestablecer(sitio = SITIO) {
  return `${String(sitio || '').trim().replace(/\/+$/, '')}${RUTA_RESTABLECER}`;
}

function partesDeUbicacion(ubicacion) {
  if (typeof ubicacion === 'string') {
    const iHash = ubicacion.indexOf('#');
    const antes = iHash >= 0 ? ubicacion.slice(0, iHash) : ubicacion;
    const hash = iHash >= 0 ? ubicacion.slice(iHash + 1) : '';
    const iQuery = antes.indexOf('?');
    let pathname = iQuery >= 0 ? antes.slice(0, iQuery) : antes;
    pathname = pathname.replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]*/i, '') || '/';
    return { pathname, search: iQuery >= 0 ? antes.slice(iQuery + 1) : '', hash };
  }
  const u = ubicacion || {};
  return {
    pathname: u.pathname || '/',
    search: String(u.search || '').replace(/^\?/, ''),
    hash: String(u.hash || '').replace(/^#/, ''),
  };
}

// URLSearchParams decodifica UNA vez y tolera un % suelto; nada de decodeURIComponent encima.
function parametros(texto) {
  try { return new URLSearchParams(texto || ''); } catch (e) { return new URLSearchParams(''); }
}

const hashEsDeAuth = (h) => ['access_token', 'refresh_token', 'error', 'error_code', 'error_description', 'sb'].some((k) => h.has(k));
const queryEsDeAuth = (q) => ['token_hash', 'error_code', 'error_description'].some((k) => q.has(k));

function numero(v) {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Contenido de un JWT sin verificarlo (solo para leer sub, email y exp). null si no se puede leer. */
export function leerJwt(token) {
  if (typeof token !== 'string') return null;
  const partes = token.split('.');
  if (partes.length !== 3 || typeof atob !== 'function') return null;
  try {
    let b64 = partes[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const binario = atob(b64);
    let json;
    try {
      json = decodeURIComponent(Array.prototype.map.call(binario, (ch) => `%${(`00${ch.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));
    } catch (e) { json = binario; }
    const contenido = JSON.parse(json);
    return contenido && typeof contenido === 'object' ? contenido : null;
  } catch (e) { return null; }
}

/** Id de la persona dueña de un JWT (sub = perfiles.id). */
export function subDelToken(token) {
  const c = leerJwt(token);
  return c && typeof c.sub === 'string' && c.sub ? c.sub : null;
}

/**
 * ¿El almacén de sesión (authStorage) guarda a esta persona? Decide el perfil guardado
 * (sb_perfil) y, si no hay perfil legible, el dueño del token guardado. Con el almacén vacío
 * o de otra persona, false: en la app instalada otra ventana pudo dejar la sesión de alguien
 * más, y escribir encima unos tokens de esta persona junto al perfil de aquella abriría la app
 * con la identidad equivocada. Lo usan la renovación (supabaseClient.js) y el cambio de
 * contraseña (cambio.logic.js, almacenTrasCambio) antes de escribir.
 * @param {{tokenGuardado?:string|null, perfilGuardado?:string|null, usuarioId?:string|null}} datos
 *   perfilGuardado es el texto de sb_perfil, tal como está en el almacén.
 */
export function almacenEsDe({ tokenGuardado = null, perfilGuardado = null, usuarioId = null } = {}) {
  if (!usuarioId) return false;
  let perfil = null;
  if (typeof perfilGuardado === 'string' && perfilGuardado) {
    try { perfil = JSON.parse(perfilGuardado); } catch (e) { perfil = null; }
  }
  const idPerfil = perfil && typeof perfil === 'object' && perfil.id ? perfil.id : null;
  return idPerfil ? idPerfil === usuarioId : subDelToken(tokenGuardado) === usuarioId;
}

function modoDeTipo(tipo) {
  return tipo === 'invite' ? 'crear' : 'nueva';
}

/**
 * Lee el regreso desde un correo de Auth.
 * @param {string|{pathname?:string, search?:string, hash?:string}} ubicacion  window.location o una URL
 * @returns {{hayEnlace:boolean, limpiar:boolean, modo:null|'nueva'|'crear', tipo:string|null,
 *   tokenHash:string|null, accessToken:string|null, refreshToken:string|null,
 *   expiraEn:number|null, expiraEl:number|null, correo:string|null,
 *   error:null|{codigo:string|null, descripcion:string|null, clase:string}}}
 *   - tokenHash: plantilla nueva; se verifica al pulsar Guardar, no al cargar.
 *   - accessToken: enlace heredado; el token ya está gastado y la sesión es esa.
 *   - error: el enlace no sirve; clase lista para textoAcceso (casi siempre enlace_vencido).
 *   - limpiar: la URL trae parámetros de Auth y hay que quitarlos (urlSinParametrosAuth).
 */
export function leerEnlaceAuth(ubicacion) {
  const { search, hash } = partesDeUbicacion(ubicacion);
  const q = parametros(search);
  const h = parametros(hash);
  const hashAuth = !!hash && hashEsDeAuth(h);
  const queryAuth = !!search && queryEsDeAuth(q);
  const base = {
    hayEnlace: hashAuth || queryAuth,
    limpiar: hashAuth || queryAuth,
    modo: null,
    tipo: null,
    tokenHash: null,
    accessToken: null,
    refreshToken: null,
    expiraEn: null,
    expiraEl: null,
    correo: null,
    error: null,
  };
  if (!base.hayEnlace) return base;

  // 1) Auth redirigió con un error (en el hash, o en la query con PKCE)
  const conError = (p) => p.has('error_code') || p.has('error_description') || p.has('error');
  const pError = hashAuth && conError(h) ? h : (queryAuth && conError(q) ? q : null);
  if (pError) {
    const codigo = pError.get('error_code') || pError.get('error') || null;
    const descripcion = pError.get('error_description') || null;
    const f = clasificarFalloAuth({
      status: ESTADO_DE_ERROR_OAUTH[pError.get('error')] || 400,
      cuerpo: { error_code: codigo, msg: descripcion || '' },
    });
    const directa = f && (f.clase === 'acceso_desactivado' || f.clase === 'servicio' || f.clase === 'limite_intentos');
    return { ...base, tipo: pError.get('type') || null, error: { codigo, descripcion, clase: directa ? f.clase : 'enlace_vencido' } };
  }

  // 2) Plantilla nueva: token_hash en la query
  if (q.has('token_hash')) {
    const tokenHash = q.get('token_hash');
    const tipo = q.get('type') || null;
    if (!tokenHash || TIPOS_CON_TOKEN_HASH.indexOf(tipo) < 0) {
      return { ...base, tipo, error: { codigo: 'enlace_invalido', descripcion: null, clase: 'enlace_vencido' } };
    }
    return { ...base, modo: modoDeTipo(tipo), tipo, tokenHash };
  }

  // 3) Enlace heredado: la sesión viene en el hash
  const accessToken = h.get('access_token');
  const tipo = h.get('type') || null;
  if (accessToken && TIPOS_CON_SESION.indexOf(tipo) >= 0) {
    const contenido = leerJwt(accessToken);
    return {
      ...base,
      modo: modoDeTipo(tipo),
      tipo,
      accessToken,
      refreshToken: h.get('refresh_token') || null,
      expiraEn: numero(h.get('expires_in')),
      expiraEl: numero(h.get('expires_at')),
      correo: contenido && typeof contenido.email === 'string' ? contenido.email : null,
    };
  }

  // 4) signup, email_change, sin tipo o un mensaje: no se usa, solo se limpia
  return { ...base, tipo };
}

/**
 * La misma dirección sin los parámetros de Auth, para history.replaceState.
 * Conserva la ruta, los parámetros ajenos y un hash que no sea de Auth.
 */
export function urlSinParametrosAuth(ubicacion) {
  const { pathname, search, hash } = partesDeUbicacion(ubicacion);
  const q = parametros(search);
  if (queryEsDeAuth(q)) CLAVES_QUERY_AUTH.forEach((k) => q.delete(k));
  const qs = q.toString();
  const hashQueda = hash && !hashEsDeAuth(parametros(hash)) ? `#${hash}` : '';
  return `${pathname || '/'}${qs ? `?${qs}` : ''}${hashQueda}`;
}

// ── Correo y contraseña ───────────────────────────────────────────────────

export function normalizarCorreo(correo) {
  return String(correo == null ? '' : correo).trim().toLowerCase();
}

export function correoValido(correo) {
  return /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/.test(normalizarCorreo(correo));
}

/**
 * Política de contraseña: una sola constante. El panel de Auth debe decir lo mismo
 * (mínimo 12, minúsculas, mayúsculas y dígitos); Auth solo cuenta letras ASCII, por
 * eso las reglas también. 72 es el tope de bcrypt, en bytes.
 */
export const POLITICA_CONTRASENA = Object.freeze({
  minimo: 12,
  maximoBytes: 72,
  reglas: Object.freeze([
    Object.freeze({ id: 'largo', texto: '12 caracteres o más' }),
    Object.freeze({ id: 'minuscula', texto: 'Una letra minúscula' }),
    Object.freeze({ id: 'mayuscula', texto: 'Una letra mayúscula' }),
    Object.freeze({ id: 'digito', texto: 'Un número' }),
  ]),
});

function bytesUtf8(texto) {
  let n = 0;
  for (const ch of texto) {
    const cp = ch.codePointAt(0);
    n += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
  }
  return n;
}

/**
 * Estado de las reglas mientras se escribe.
 * @returns {{reglas:{id:string, texto:string, cumple:boolean}[], faltan:string[], excedeMaximo:boolean, cumple:boolean}}
 */
export function reglasContrasena(nueva, politica = POLITICA_CONTRASENA) {
  const s = typeof nueva === 'string' ? nueva : '';
  const cumplidas = {
    largo: Array.from(s).length >= politica.minimo,
    minuscula: /[a-z]/.test(s),
    mayuscula: /[A-Z]/.test(s),
    digito: /[0-9]/.test(s),
  };
  const reglas = politica.reglas.map((r) => ({ id: r.id, texto: r.texto, cumple: !!cumplidas[r.id] }));
  const faltan = reglas.filter((r) => !r.cumple).map((r) => r.id);
  const excedeMaximo = bytesUtf8(s) > politica.maximoBytes;
  return { reglas, faltan, excedeMaximo, cumple: faltan.length === 0 && !excedeMaximo };
}

/** Lo que falta para Entrar. null = se puede enviar. La contraseña nunca se recorta. */
export function validarEntrar({ correo, contrasena } = {}) {
  if (!normalizarCorreo(correo)) return { clase: 'falta_correo' };
  if (!correoValido(correo)) return { clase: 'correo_invalido' };
  if (!contrasena) return { clase: 'falta_contrasena' };
  return null;
}

/** Lo que falta para pedir el enlace. */
export function validarRecuperar({ correo } = {}) {
  if (!normalizarCorreo(correo)) return { clase: 'falta_correo' };
  if (!correoValido(correo)) return { clase: 'correo_invalido' };
  return null;
}

/**
 * Lo que falta para guardar una contraseña (nueva, crear o cambiar).
 * @param {{nueva?:string, confirmacion?:string, actual?:string, pedirActual?:boolean}} datos
 * @returns {null|{clase:string, faltan?:string[]}}
 */
export function validarContrasenaNueva({ nueva, confirmacion, actual, pedirActual = false } = {}) {
  if (pedirActual && !actual) return { clase: 'falta_actual' };
  if (!nueva) return { clase: 'falta_contrasena' };
  const r = reglasContrasena(nueva);
  if (r.excedeMaximo) return { clase: 'contrasena_larga' };
  if (!r.cumple) return { clase: 'contrasena_debil', faltan: r.faltan };
  if (pedirActual && actual === nueva) return { clase: 'contrasena_repetida' };
  if (!confirmacion) return { clase: 'falta_confirmacion' };
  if (confirmacion !== nueva) return { clase: 'no_coinciden' };
  return null;
}

// ── Perfil ────────────────────────────────────────────────────────────────

/**
 * Decide con la respuesta de cargarPerfil(id) (arreglo de filas o { error }).
 * Pendiente se revisa antes que activo, como siempre. Un error de red (__network) es
 * sin_conexion; cualquier otro, incluida la lectura que se quedó sin respuesta dentro de su
 * tiempo límite (acceso/perfil.js), es servicio.
 * @returns {{estado:'ok'|'sin_perfil'|'pendiente'|'acceso_desactivado'|'sin_conexion'|'servicio', perfil:Object|null}}
 *   estado distinto de ok es una clase para textoAcceso.
 */
export function decidirPerfil(respuesta) {
  if (!Array.isArray(respuesta)) {
    const deRed = !!(respuesta && respuesta.error && respuesta.__network);
    return { estado: deRed ? 'sin_conexion' : 'servicio', perfil: null };
  }
  const perfil = respuesta[0];
  if (!perfil) return { estado: 'sin_perfil', perfil: null };
  if (perfil.rol === 'pendiente') return { estado: 'pendiente', perfil };
  if (perfil.activo === false) return { estado: 'acceso_desactivado', perfil };
  return { estado: 'ok', perfil };
}

/** Iniciales del avatar, tolerante a nombre o apellido vacíos. */
export function iniciales(perfil) {
  const p = perfil || {};
  const primera = (s) => {
    const t = String(s == null ? '' : s).trim();
    return t ? Array.from(t)[0] : '';
  };
  const palabras = String(p.nombre == null ? '' : p.nombre).trim().split(/\s+/).filter(Boolean);
  let r = primera(palabras[0]) + primera(p.apellido || palabras[1]);
  if (!r) r = primera(p.email);
  return r.toUpperCase();
}

// ── Bloqueo local ─────────────────────────────────────────────────────────
// Solo suman las credenciales incorrectas: sin red, con el servicio caído o con el
// límite del servidor nadie se equivocó de contraseña.

export const BLOQUEO_LOCAL = Object.freeze({ fallos: 3, segundos: 30 });
export const BLOQUEO_INICIAL = Object.freeze({ fallos: 0, hasta: 0 });

/** Estado siguiente tras un intento fallido clasificado. Al tercero bloquea y vuelve a cero. */
export function registrarFallo(estado, clasificacion, ahora) {
  const e = estado || BLOQUEO_INICIAL;
  if (!clasificacion || !clasificacion.cuentaParaBloqueo) return e;
  const fallos = (e.fallos || 0) + 1;
  if (fallos >= BLOQUEO_LOCAL.fallos) return { fallos: 0, hasta: ahora + BLOQUEO_LOCAL.segundos * 1000 };
  return { fallos, hasta: e.hasta || 0 };
}

/** Segundos que faltan del bloqueo (para «Espera N s» en el botón). 0 = libre. */
export function segundosDeBloqueo(estado, ahora) {
  const hasta = (estado && estado.hasta) || 0;
  return hasta > ahora ? Math.ceil((hasta - ahora) / 1000) : 0;
}
